import React, { useEffect, useState } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send, 
  MessageSquare,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.trackyourwheelz.com/api';

const contactMethods = [
  {
    icon: Mail,
    title: 'Email Us',
    value: 'support@trackyourwheelz.com',
    action: 'mailto:support@trackyourwheelz.com',
  },
  // {
  //   icon: Phone,
  //   title: 'Call Us',
  //   value: '+91 1234567890',
  //   action: 'tel:+911234567890',
  // },
  // {
  //   icon: MapPin,
  //   title: 'Visit Us',
  //   value: 'PLOT NO 75, Vishwakarma Colony, Suraram, Hyderabad',
  //   action: '#',
  // },
  {
    icon: Clock,
    title: 'Business Hours',
    value: 'Monday - Friday: 9:00 AM - 6:00 PM IST',
    action: '#',
  },
];

const faqs = [
  {
    question: 'How quickly will I receive a response?',
    answer: 'We aim to respond to all inquiries within 24 hours during business days. For urgent matters, please call our support line.'
  },
  {
    question: 'Do you offer technical support?',
    answer: 'Yes! Our technical support team is available 24/7 to help with any platform-related issues or questions.'
  },
  {
    question: 'Can I schedule a demo?',
    answer: 'Absolutely! Contact us through this form or email us directly to schedule a personalized demo of our platform.'
  },
  {
    question: 'What are your business hours?',
    answer: 'Our office is open Monday through Friday, 9:00 AM to 6:00 PM IST. However, our support team is available 24/7.'
  }
];

const ContactPage = () => {
  const [theme, setTheme] = useState('light');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    enquiryType: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionFeedback, setSubmissionFeedback] = useState(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      enquiryType: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionFeedback(null);

    const payload = {
      name: formData.name.trim(),
      related_to: formData.subject.trim(),
      contact_number: formData.phone.trim(),
      enquiry_type: formData.enquiryType,
      description: formData.message.trim()
    };

    try {
      const response = await fetch(`${API_BASE_URL}/callback-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok || data?.success !== true) {
        throw new Error(data?.message || 'Failed to submit callback request');
      }

      setSubmissionFeedback({
        type: 'success',
        message: data.message || 'Callback request submitted successfully.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        enquiryType: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmissionFeedback({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] text-slate-100'
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Contact Us" />

        <div className="absolute inset-0 pointer-events-none">
          {theme === 'dark' ? (
            <>
              <div className="absolute -top-24 -left-24 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/3 right-10 w-72 h-72 bg-indigo-500/30 rounded-full blur-[140px] animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-[160px] animate-pulse delay-500" />
            </>
          ) : (
            <>
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
            </>
          )}
        </div>

        <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${theme === 'dark' ? 'bg-slate-900 border-1 border-muted-foreground/20 text-slate-100' : 'bg-white border-1 border-muted-foreground/20 text-slate-700'}`}>
                <MessageSquare className="h-4 w-4" />
                Get in Touch
              </div>
              <h1 className="text-sm sm:text-4xl font-bold">Contact Us</h1>
              <p className={`text-sm sm:text-lg ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                Have questions about TrackYourWheelz? Want to know more about our services? 
                Reach out to us and our team will get back to you promptly.
              </p>
            </header>

            {/* Contact Methods */}
            <section className={`rounded-xl border-1 border-muted-foreground/20 ${theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'} p-6 space-y-4`}>
              <h2 className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-500" />
                Ways to Reach Us
              </h2>
              <div className="space-y-4">
                {contactMethods.map((method) => {
                  const Icon = method.icon;
                  return (
                    <div
                      key={method.title}
                      className={`flex flex-col sm:flex-row sm:items-start sm:gap-4 rounded-lg  ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'} p-4`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${theme === 'dark' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 mt-3 sm:mt-0">
                        <h3 className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{method.title}</h3>
                        {method.action !== '#' ? (
                          <a
                            href={method.action}
                            className={`text-sm block hover:underline ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}
                          >
                            {method.value}
                          </a>
                        ) : (
                          <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                            {method.value}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Contact Form */}
            <section className={`rounded-xl border-1 border-muted-foreground/20 ${theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'} p-6 space-y-4`}>
              <h2 className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-500" />
                Request a Call Back
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                Fill out the form below and we'll respond as soon as possible.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className={theme === 'dark' ? 'text-slate-200' : 'text-gray-700'}>
                      Name 
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className={`${
                        theme === 'dark'
                          ? 'bg-slate-800 border-1 border-muted-foreground/20 text-white'
                          : 'bg-white border-1 border-muted-foreground/20'
                      }`}
                      placeholder="Your name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className={theme === 'dark' ? 'text-slate-200' : 'text-gray-700'}>
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className={`${
                      theme === 'dark'
                        ? 'bg-slate-800 border-1 border-muted-foreground/20 text-white'
                        : 'bg-white border-1 border-muted-foreground/20'
                    }`}
                    placeholder="+91 1234567890"
                  />
                </div>
                <div className="space-y-2 w-full">
                  <Label htmlFor="enquiryType" className={theme === 'dark' ? 'text-slate-200' : 'text-gray-700'}>
                    Enquiry Type
                  </Label>
                  <Select value={formData.enquiryType} onValueChange={handleSelectChange} required>
                    <SelectTrigger
                      className={`w-full h-10 ${
                        theme === 'dark'
                          ? 'bg-slate-800 border-1 border-muted-foreground/20 text-white'
                          : 'bg-white border-1 border-muted-foreground/20'
                      }`}
                    >
                      <SelectValue placeholder="Select enquiry type" />
                    </SelectTrigger>
                    <SelectContent
                      className={theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white'}
                    >
                      <SelectItem value="Become a partner">Become a partner</SelectItem>
                      <SelectItem value="Purchase Product">Purchase Product</SelectItem>
                      <SelectItem value="Technical Support">Technical Support</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className={theme === 'dark' ? 'text-slate-200' : 'text-gray-700'}>
                    Related To
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className={`${
                      theme === 'dark'
                        ? 'bg-slate-800 border-1 border-muted-foreground/20 text-white'
                        : 'bg-white border-1 border-muted-foreground/20'
                    }`}
                    placeholder="What's this about?"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className={theme === 'dark' ? 'text-slate-200' : 'text-gray-700'}>
                    Description 
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className={`${
                      theme === 'dark'
                        ? 'bg-slate-800 border-1 border-muted-foreground/20 text-white'
                        : 'bg-white border-1 border-muted-foreground/20'
                    }`}
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>
                {submissionFeedback && (
                  <div
                    className={`text-sm rounded-md p-3 ${
                      submissionFeedback.type === 'success'
                        ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/40'
                        : 'bg-red-500/10 text-red-700 border border-red-500/40'
                    }`}
                  >
                    {submissionFeedback.message}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full h-12 text-white ${
                    theme === 'dark'
                      ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] hover:from-[#5a4da3] hover:to-[#3d2f6e]'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                  }`}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </section>

            {/* FAQ Section */}
            <section className={`rounded-xl border-1 border-muted-foreground/20 ${theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'} p-6 space-y-4`}>
              <h2 className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-500" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="space-y-2">
                    <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {faq.question}
                    </h3>
                    <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Footer */}
            <footer className={`rounded-xl  ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'} p-6 space-y-4`}>
              <h2 className="text-sm sm:text-lg font-semibold">Need more help?</h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                Our support team is available 24/7 to assist you with any questions or concerns. 
                We typically respond within 24 hours during business days.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => window.open('mailto:support@trackyourwheelz.com', '_blank')} 
                  className="sm:w-auto h-12 px-6 text-base border-1 border-muted-foreground/20 text-foreground hover:bg-muted/50"
                  size="lg"
                  variant="outline"
                >
                  Email Support Team
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.history.back()}
                  className="sm:w-auto h-12 px-6 text-base border-1 border-muted-foreground/20 text-foreground hover:bg-muted/50"
                  size="lg"
                >
                  Go Back
                </Button>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default ContactPage;
